import { Direction, Facing } from "../movement/step.ts";

/**
 * The aim an `act` carries (ADR-073 decision 7): a direction in 65,536ths of a turn, 0 pointing right (+x), 16,384 down
 * (+y), 32,768 left and 49,152 up, so it grows clockwise on the screen. A resolved act turns its caster to the nearest
 * of the four facings, by the same rule on the page and the map server, so the page turns its character on intent and
 * needs no correction.
 */

/**
 * The aims of the four facings.
 */
export const AIM_RIGHT = 0;
export const AIM_DOWN = 16384;
export const AIM_LEFT = 32768;
export const AIM_UP = 49152;

const TURN = 65536;
const EIGHTH = 8192;
const RADIANS_PER_UNIT = (2 * Math.PI) / TURN;

/**
 * The x of an aim's unit vector, exact at the four facings.
 *
 * @param aim The aim, 0 to 65,535.
 * @returns The cosine of the aim's angle.
 */
export function aimX(aim: number): number {
  if (aim === AIM_RIGHT) return 1;
  if (aim === AIM_LEFT) return -1;
  if (aim === AIM_DOWN || aim === AIM_UP) return 0;
  return Math.cos(aim * RADIANS_PER_UNIT);
}

/**
 * The y of an aim's unit vector, down positive, exact at the four facings.
 *
 * @param aim The aim, 0 to 65,535.
 * @returns The sine of the aim's angle.
 */
export function aimY(aim: number): number {
  if (aim === AIM_DOWN) return 1;
  if (aim === AIM_UP) return -1;
  if (aim === AIM_RIGHT || aim === AIM_LEFT) return 0;
  return Math.sin(aim * RADIANS_PER_UNIT);
}

/**
 * The facing an aim turns its caster to: right below 8,192 or from 57,344, down from 8,192 to below 24,576, left from
 * 24,576 to below 40,960, up from 40,960 to below 57,344.
 *
 * @param aim The aim, 0 to 65,535.
 * @returns The facing.
 */
export function facingOfAim(aim: number): Facing {
  const quadrant = Math.floor((aim + EIGHTH) / (2 * EIGHTH)) % 4;
  return quadrant === 0 ? Facing.Right : quadrant === 1 ? Facing.Down : quadrant === 2 ? Facing.Left : Facing.Up;
}

/**
 * The aim toward a point from an origin (P2.1-T4): the whole 65,536th of a turn nearest the direction of the offset,
 * y growing down the screen, so a click's aim is measured as the map server reads it.
 *
 * @param dx The point's offset right of the origin, in any unit.
 * @param dy The point's offset below the origin, in the same unit.
 * @returns The aim, 0 to 65,535, or null when the point is the origin itself and names no direction.
 * @example
 * aimToward(5, 5);
 * // 8192, down and to the right
 */
export function aimToward(dx: number, dy: number): number | null {
  if (dx === 0 && dy === 0) {
    return null;
  }
  const units = Math.round(Math.atan2(dy, dx) / RADIANS_PER_UNIT);
  return ((units % TURN) + TURN) % TURN;
}

/**
 * The aim of a facing's own direction, which `facingOfAim` turns back into the facing.
 *
 * @param facing The facing.
 * @returns The aim.
 */
export function aimOfFacing(facing: Facing): number {
  return facing === Facing.Right ? AIM_RIGHT : facing === Facing.Down ? AIM_DOWN : facing === Facing.Left ? AIM_LEFT : AIM_UP;
}

/**
 * The aim of each movement intent, by `Direction`'s number: none for no intent, and each other its own direction in
 * exact eighths of a turn.
 */
const AIM_OF_DIRECTION: readonly (number | null)[] = [null, AIM_UP, AIM_DOWN, AIM_LEFT, AIM_RIGHT, AIM_LEFT + EIGHTH, AIM_UP + EIGHTH, AIM_DOWN + EIGHTH, AIM_RIGHT + EIGHTH];

/**
 * The aim of a movement intent's own direction (ADR-073's amendment "the way a displacement goes, and a swing keeps
 * its facing"): a displacement goes the way the character is moving, so the page aims it by the intent held, and
 * along the facing when none is.
 *
 * @param direction The movement intent.
 * @returns The aim, 0 to 65,535, or null for no intent.
 * @example
 * aimOfDirection(Direction.UpLeft);
 * // 40960, up and to the left
 */
export function aimOfDirection(direction: Direction): number | null {
  return AIM_OF_DIRECTION[direction] ?? null;
}
